import {useParams, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/store';
import {fetchOccasions, filterSingleOccasionSelector} from '../../store/slices/occasions_slice';
import styles from './occasion_details.module.scss';
import {Main} from '../../components/main/main.tsx';
import mainStyles from '../../components/main/main.module.scss';
import {Button} from '../../components/button/button.tsx';
import {ButtonPosition} from '../../enums/button_position.ts';
import {__} from '../../support/lang/translations.ts';
import {FlexColumn} from '../../components/utility/flex_column.tsx';
import {FlexRow} from '../../components/utility/flex_row.tsx';
import {Alignment} from '../../enums/alignment.ts';
import {AltButton} from '../../components/button/alt_button.tsx';
import {formatKm} from '../../support/format_distance.ts';
import {formatEuro} from '../../support/format_currency.ts';
import {General} from './tabs/general.tsx';
import {History} from './tabs/history.tsx';
import {Options} from './tabs/options.tsx';
import {ImagesGrid} from './components/images_grid.tsx';
// import {useEffect} from 'react';
import {ShareButton} from './components/share_button.tsx';
import {ButtonTabs} from '../../components/tabs/button_tabs.tsx';
import {Technical} from './tabs/technical.tsx';
import {Financial} from './tabs/financial.tsx';
import {Environment} from './tabs/environment.tsx';
import {Description} from './tabs/description.tsx';
import {useEffect} from 'react';

export function OccasionDetails() {
    const dispatch = useAppDispatch();
    const params = useParams<'occasionId'>();
    const occasionId = parseInt(params.occasionId ?? '-1', 10);
    const occasion = useAppSelector((state) => filterSingleOccasionSelector(state, occasionId));
    const locations = useAppSelector((state) => state.appConfig.locations);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        dispatch(fetchOccasions());
    }, []);

    if (!occasion) {
        return null;
    }

    const location = locations?.find((location) => location?.id === occasion.location_id) ?? null;

    return (
        <Main className={mainStyles.bg_white}>
            <div className={styles.occasion_details}>
                <nav className={styles.occasion_details__nav}>
                    <div className={styles.title__buttons}>
                        <FlexRow>
                            <FlexColumn alignment={Alignment.Start}>
                                <Button
                                    to={`/occasions?${searchParams.toString()}`}
                                    position={ButtonPosition.Right}
                                    noBorder={true}
                                    preventScrollReset={true}
                                >
                                    <i className="fa-light fa-chevron-left" />
                                    {__('occasions.details.buttons.back')}
                                </Button>
                            </FlexColumn>
                            <FlexColumn alignment={Alignment.End}>
                                <FlexRow>
                                    <ShareButton occasion={occasion} />
                                    {location?.email && (
                                        <AltButton to={`mailto:${location.email}`}>
                                            <i className="fa-light fa-envelope" />
                                        </AltButton>
                                    )}
                                    {location?.phone_number && (
                                        <AltButton to={`tel:${location.phone_number}`}>
                                            <i className="fa-light fa-phone" />
                                        </AltButton>
                                    )}
                                </FlexRow>
                            </FlexColumn>
                        </FlexRow>
                    </div>
                </nav>
                <div className={styles.occasion_details__title}>
                    <div className={styles.title__wrapper}>
                        <FlexRow>
                            <h2>
                                {occasion.brand} {occasion.model}
                            </h2>

                            <span className={styles.occasion_price}>
                                {occasion?.price != null && occasion?.price !== 0 && (
                                    <span className={styles.full_price}>{formatEuro(occasion.price)}</span>
                                )}
                                {occasion?.price && occasion?.financing?.lease_price ? <span>/</span> : null}
                                {occasion?.financing?.lease_price ? (
                                    <span className={styles.lease_price}>
                                        {formatEuro(occasion.financing.lease_price)} p.m.
                                    </span>
                                ) : null}
                            </span>
                        </FlexRow>
                        <FlexRow>
                            <h3>{occasion.type}</h3>
                        </FlexRow>
                        <FlexRow>
                            <h4>
                                {occasion.build_year}, {occasion.fuel}, {occasion.transmission}, {occasion.color},{' '}
                                {occasion.bodywork}
                            </h4>
                        </FlexRow>
                    </div>
                </div>

                <div className={styles.occasion_details__images}>
                    <ImagesGrid occasion={occasion} />
                </div>

                <div className={styles.occasion_details__details}>
                    <div className={styles.details_title}>
                        <h2>
                            {__('occasions.details.titles.details')}
                            {occasion.brand}
                        </h2>
                    </div>
                    <div className={styles.details_intro}>
                        <p>
                            Deze{' '}
                            <strong>
                                {occasion.brand} {occasion.model}
                            </strong>
                            {occasion.type ? ` ${occasion.type}` : ''} is een{' '}
                            {occasion.bodywork ? <strong>{occasion.bodywork.toLowerCase()}</strong> : 'voertuig'} uit{' '}
                            <strong>{occasion.build_year}</strong> in de kleur{' '}
                            <strong>{occasion.color ?? 'onbekend'}</strong>. Met{' '}
                            <strong>{occasion.mileage ? formatKm(occasion.mileage) : 'onbekend'}</strong> op de teller
                            en een <strong>{occasion.fuel?.toLowerCase() ?? 'onbekende'}</strong> motor met{' '}
                            <strong>{occasion.transmission?.toLowerCase() ?? 'onbekende'}</strong> versnellingsbak is
                            dit een uitstekende keuze.{' '}
                            {occasion.price ? (
                                <>
                                    De vraagprijs bedraagt <strong>{formatEuro(occasion.price)}</strong>
                                    {occasion.financing?.lease_price ? (
                                        <>
                                            , of lease-it vanaf{' '}
                                            <strong>{formatEuro(occasion.financing.lease_price)} p.m.</strong>
                                        </>
                                    ) : null}
                                    .{' '}
                                </>
                            ) : null}
                            Bekijk de tabs hieronder voor alle technische details, opties en meer.
                        </p>
                        <div className={styles.buttons_wrapper}>
                            <Button to={`/occasion/${occasion?.id}/offer`} noIcon={true} preventScrollReset={true}>
                                <span>{__('occasions.details.buttons.offer')}</span>
                            </Button>
                            <Button to={`/occasion/${occasion?.id}/testdrive`} noIcon={true}>
                                <span>{__('occasions.details.buttons.test_drive')}</span>
                            </Button>
                            <Button to={`/occasion/${occasion?.id}/tradein`} noIcon={true}>
                                <span>{__('occasions.details.buttons.trade_in')}</span>
                            </Button>
                        </div>
                    </div>
                </div>

                <div className={styles.occasion_details__tabs}>
                    <ButtonTabs
                        wide={true}
                        disabled={false}
                        name="general"
                        tabs={[
                            {
                                id: 'general',
                                link: <span>{__('occasions.details.tabs.general')}</span>,
                                child: <General occasion={occasion} />,
                            },
                            {
                                id: 'technical',
                                link: <span>{__('occasions.details.tabs.technical')}</span>,
                                child: <Technical occasion={occasion} />,
                            },
                            {
                                id: 'history',
                                link: <span>{__('occasions.details.tabs.history')}</span>,
                                child: <History occasion={occasion} />,
                            },
                            {
                                id: 'financial',
                                link: <span>{__('occasions.details.tabs.financial')}</span>,
                                child: <Financial occasion={occasion} />,
                            },
                            {
                                id: 'environment',
                                link: <span>{__('occasions.details.tabs.environment')}</span>,
                                child: <Environment occasion={occasion} />,
                            },
                            {
                                id: 'options',
                                link: <span>{__('occasions.details.tabs.options')}</span>,
                                child: <Options occasion={occasion} />,
                            },
                        ]}
                    />
                </div>
                <div className={styles.occasion_details__details} style={{marginTop: 0}}>
                    <div className={styles.details_title}>
                        <h2>
                            {__('occasions.details.titles.other')}
                            {occasion.brand}
                        </h2>
                    </div>

                    <Description occasion={occasion} />
                </div>
            </div>
        </Main>
    );
}
